import {
  buffer,
  circle,
  difference,
  destination,
  intersect,
  lineString,
  polygon,
  union,
  centroid
} from "@turf/turf";
import type { Feature, Polygon, MultiPolygon, Position } from "geojson";
import type { DistanceUnit, Measurement } from "../types";

const BEARING_LENGTH_KM = 120;
const BEARING_CORRIDOR_KM = 0.8;

const toKilometers = (value: number, unit: DistanceUnit) => {
  if (unit === "km") return value;
  if (unit === "mi") return value * 1.60934;
  return value / 1000;
};

const EARTH_RADIUS_M = 6371000;
const toRadians = (value: number) => (value * Math.PI) / 180;
const toDegrees = (value: number) => (value * 180) / Math.PI;

export const normalizeBearing = (bearing: number) => {
  const normalized = ((bearing % 360) + 360) % 360;
  return normalized;
};

export const bearingToTrue = (measurement: Measurement) => {
  if (measurement.type !== "bearing") return 0;
  if (measurement.northReference === "true") return normalizeBearing(measurement.value);
  return normalizeBearing(measurement.value + measurement.declination);
};

export type MeasurementGeometry = {
  polygon: Feature<Polygon | MultiPolygon>;
  line?: Position[];
  circleRadiiMeters?: number[];
};

const buildBearingConePolygon = (
  origin: Position,
  bearing: number,
  errorDeg: number
): Feature<Polygon | MultiPolygon> | null => {
  const clampedError = Math.min(Math.max(errorDeg, 0), 180);
  if (clampedError <= 0) return null;
  if (clampedError >= 180) {
    return circle(origin, BEARING_LENGTH_KM, {
      steps: 80,
      units: "kilometers"
    });
  }

  const arcDegrees = clampedError * 2;
  const steps = Math.max(12, Math.ceil(arcDegrees / 5));
  const step = arcDegrees / steps;
  const arc: Position[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const angle = normalizeBearing(bearing - clampedError + step * i);
    const point = destination(origin, BEARING_LENGTH_KM, angle, {
      units: "kilometers"
    });
    arc.push(point.geometry.coordinates);
  }

  return polygon([[origin, ...arc, origin]]);
};

export const measurementToGeometry = (
  measurement: Measurement
): MeasurementGeometry | null => {
  if (measurement.type === "distance") {
    const radiusKm = toKilometers(measurement.value, measurement.units);
    const errorPct = Math.min(Math.max(measurement.distanceErrorPct ?? 0, 0), 100);
    if (errorPct > 0) {
      const deltaKm = radiusKm * (errorPct / 100);
      const innerRadiusKm = Math.max(0, radiusKm - deltaKm);
      const outerRadiusKm = radiusKm + deltaKm;
      const outerCircle = circle([measurement.lon, measurement.lat], outerRadiusKm, {
        steps: 80,
        units: "kilometers"
      });
      if (innerRadiusKm > 0) {
        const innerCircle = circle([measurement.lon, measurement.lat], innerRadiusKm, {
          steps: 80,
          units: "kilometers"
        });
        const ring = difference(outerCircle, innerCircle) as Feature<
          Polygon | MultiPolygon
        > | null;
        return {
          polygon: ring ?? outerCircle,
          circleRadiiMeters: [innerRadiusKm * 1000, outerRadiusKm * 1000]
        };
      }
      return {
        polygon: outerCircle,
        circleRadiiMeters: [outerRadiusKm * 1000]
      };
    }

    const polygon = circle([measurement.lon, measurement.lat], radiusKm, {
      steps: 80,
      units: "kilometers"
    });
    return {
      polygon,
      circleRadiiMeters: [radiusKm * 1000]
    };
  }

  const bearing = bearingToTrue(measurement);
  const start: Position = [measurement.lon, measurement.lat];
  const endPoint = destination(start, BEARING_LENGTH_KM, bearing, {
    units: "kilometers"
  });
  const line = lineString([start, endPoint.geometry.coordinates]);
  const cone = buildBearingConePolygon(start, bearing, measurement.bearingErrorDeg ?? 0);
  const polygon = cone ?? buffer(line, BEARING_CORRIDOR_KM / 2, {
    units: "kilometers"
  });

  return {
    polygon,
    line: line.geometry.coordinates
  };
};

export const intersectAll = (
  polygons: Feature<Polygon | MultiPolygon>[]
): Feature<Polygon | MultiPolygon> | null => {
  if (polygons.length === 0) return null;
  let current: Feature<Polygon | MultiPolygon> | null = polygons[0];
  for (let i = 1; i < polygons.length; i += 1) {
    if (!current) return null;
    const next = intersect(current, polygons[i]);
    if (!next) return null;
    current = next as Feature<Polygon | MultiPolygon>;
  }
  return current;
};

export const unionAll = (
  polygons: Feature<Polygon | MultiPolygon>[]
): Feature<Polygon | MultiPolygon> | null => {
  if (polygons.length === 0) return null;
  let current: Feature<Polygon | MultiPolygon> | null = polygons[0];
  for (let i = 1; i < polygons.length; i += 1) {
    if (!current) return null;
    const next = union(current, polygons[i]);
    if (!next) return null;
    current = next as Feature<Polygon | MultiPolygon>;
  }
  return current;
};

export type ConfidenceRegions = {
  all: Feature<Polygon | MultiPolygon> | null;
  allButOne: Feature<Polygon | MultiPolygon> | null;
  union: Feature<Polygon | MultiPolygon> | null;
};

export const computeConfidenceRegions = (
  geometries: Feature<Polygon | MultiPolygon>[]
): ConfidenceRegions => {
  const all = intersectAll(geometries);

  let allButOne: Feature<Polygon | MultiPolygon> | null = null;
  if (geometries.length > 2) {
    geometries.forEach((_, index) => {
      const subset = geometries.filter((__, idx) => idx !== index);
      const intersection = intersectAll(subset);
      if (intersection) {
        allButOne = allButOne ? (union(allButOne, intersection) as Feature<
          Polygon | MultiPolygon
        >) : intersection;
      }
    });
  }

  const combined = unionAll(geometries);

  return {
    all,
    allButOne,
    union: combined
  };
};

type LonLat = [number, number];

const isPointOnSegment = (point: LonLat, start: Position, end: Position) => {
  const [px, py] = point;
  const [x1, y1] = start;
  const [x2, y2] = end;
  const cross = (px - x1) * (y2 - y1) - (py - y1) * (x2 - x1);
  if (Math.abs(cross) > 1e-12) return false;

  const dot = (px - x1) * (px - x2) + (py - y1) * (py - y2);
  return dot <= 1e-12;
};

const isPointInRing = (point: LonLat, ring: Position[]) => {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const start = ring[j];
    const end = ring[i];
    if (isPointOnSegment(point, start, end)) return true;

    const [xi, yi] = end;
    const [xj, yj] = start;
    const intersects =
      (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

const isPointInPolygonCoordinates = (
  point: LonLat,
  coordinates: Polygon["coordinates"]
) => {
  if (coordinates.length === 0) return false;
  if (!isPointInRing(point, coordinates[0])) return false;

  for (let i = 1; i < coordinates.length; i += 1) {
    if (isPointInRing(point, coordinates[i])) return false;
  }
  return true;
};

const isPointInRegion = (
  point: LonLat,
  geometry: Polygon | MultiPolygon
) => {
  if (geometry.type === "Polygon") {
    return isPointInPolygonCoordinates(point, geometry.coordinates);
  }
  return geometry.coordinates.some((polygonCoordinates) =>
    isPointInPolygonCoordinates(point, polygonCoordinates)
  );
};

const estimateInsidePolygon = (coordinates: Polygon["coordinates"]) => {
  const componentCenter = centroid(polygon(coordinates));
  const [lon, lat] = componentCenter.geometry.coordinates;
  if (isPointInPolygonCoordinates([lon, lat], coordinates)) {
    return { lat, lon };
  }

  const fallback = coordinates[0]?.[0];
  if (fallback) {
    const [fallbackLon, fallbackLat] = fallback;
    return { lat: fallbackLat, lon: fallbackLon };
  }
  return null;
};

export const estimateCenters = (
  region: Feature<Polygon | MultiPolygon>
) => {
  const polygonCoordinates =
    region.geometry.type === "Polygon"
      ? [region.geometry.coordinates]
      : region.geometry.coordinates;

  const estimates = polygonCoordinates
    .map((coordinates) => estimateInsidePolygon(coordinates))
    .filter(
      (
        estimate
      ): estimate is {
        lat: number;
        lon: number;
      } => Boolean(estimate)
    );

  if (estimates.length === 0) {
    return [estimateCenter(region)];
  }

  const deduped: { lat: number; lon: number }[] = [];
  estimates.forEach((candidate) => {
    const existing = deduped.find(
      (item) =>
        Math.abs(item.lat - candidate.lat) < 1e-9 &&
        Math.abs(item.lon - candidate.lon) < 1e-9
    );
    if (!existing) {
      deduped.push(candidate);
    }
  });
  return deduped;
};

export const estimateCenter = (
  region: Feature<Polygon | MultiPolygon>
) => {
  const center = centroid(region);
  const [centerLon, centerLat] = center.geometry.coordinates;
  if (isPointInRegion([centerLon, centerLat], region.geometry)) {
    const [lon, lat] = center.geometry.coordinates;
    return { lat, lon };
  }

  const polygonCoordinates =
    region.geometry.type === "Polygon"
      ? [region.geometry.coordinates]
      : region.geometry.coordinates;
  for (const coordinates of polygonCoordinates) {
    const estimate = estimateInsidePolygon(coordinates);
    if (estimate) return estimate;
  }

  return { lat: centerLat, lon: centerLon };
};

export type CircleIntersection = {
  lat: number;
  lon: number;
};

type ProjectedPoint = {
  x: number;
  y: number;
};

const projectToMeters = (lat: number, lon: number, refLatRad: number) => {
  const latRad = toRadians(lat);
  const lonRad = toRadians(lon);
  return {
    x: lonRad * Math.cos(refLatRad) * EARTH_RADIUS_M,
    y: latRad * EARTH_RADIUS_M
  };
};

const unprojectToLatLon = (point: ProjectedPoint, refLatRad: number) => {
  const lat = toDegrees(point.y / EARTH_RADIUS_M);
  const lon = toDegrees(point.x / (EARTH_RADIUS_M * Math.cos(refLatRad)));
  return { lat, lon };
};

export const circleIntersections = (
  first: Measurement,
  second: Measurement
): CircleIntersection[] => {
  if (first.type !== "distance" || second.type !== "distance") return [];

  const r0 = toKilometers(first.value, first.units) * 1000;
  const r1 = toKilometers(second.value, second.units) * 1000;
  const refLatRad = toRadians((first.lat + second.lat) / 2);
  const p0 = projectToMeters(first.lat, first.lon, refLatRad);
  const p1 = projectToMeters(second.lat, second.lon, refLatRad);

  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const d = Math.hypot(dx, dy);

  if (d === 0) return [];
  if (d > r0 + r1) return [];
  if (d < Math.abs(r0 - r1)) return [];

  const a = (r0 * r0 - r1 * r1 + d * d) / (2 * d);
  const hSquared = r0 * r0 - a * a;
  if (hSquared < 0) return [];

  const h = Math.sqrt(hSquared);
  const x2 = p0.x + (a * dx) / d;
  const y2 = p0.y + (a * dy) / d;

  const rx = (-dy * h) / d;
  const ry = (dx * h) / d;

  const intersection1 = unprojectToLatLon({ x: x2 + rx, y: y2 + ry }, refLatRad);
  const intersection2 = unprojectToLatLon({ x: x2 - rx, y: y2 - ry }, refLatRad);

  if (Math.abs(h) < 0.001) {
    return [intersection1];
  }

  return [intersection1, intersection2];
};
